import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { ChangeEvent, CSSProperties, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

interface AgeOption {
  value: number;
  label: string;
}

interface UseAgeDropdownConfig {
  options: AgeOption[];
  minAge: number;
  maxAge: number;
  onSelect: (age: number) => void;
}

export const useAgeDropdown = ({ options, minAge, maxAge, onSelect }: UseAgeDropdownConfig) => {
  const { shadowRoot, containerEl } = useShadowRoot();

  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsEditing(false);
  }, []);

  const handleClickOutside = useCallback(
    (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    shadowRoot.addEventListener("mousedown", handleClickOutside);
    return () => shadowRoot.removeEventListener("mousedown", handleClickOutside);
  }, [shadowRoot, handleClickOutside]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const filteredOptions = inputValue
    ? options.filter((opt) => String(opt.value).startsWith(inputValue))
    : options;

  const handleSelect = (age: number) => {
    onSelect(age);
    close();
  };

  const commitTypedValue = () => {
    const num = parseInt(inputValue, 10);
    if (!isNaN(num) && num >= minAge && num <= maxAge) {
      handleSelect(num);
    } else {
      close();
    }
  };

  const open = () => {
    if (!isOpen) updatePosition();
    setIsOpen(true);
    setIsEditing(true);
    setInputValue("");
    setHighlightedIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex].value);
      } else {
        commitTypedValue();
      }
    } else if (e.key === "Escape") {
      close();
    } else if (
      !/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
    setInputValue(val);
    setHighlightedIndex(0);
  };

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const idx = highlightedIndex;
    if (idx >= 0 && idx < filteredOptions.length) {
      const el = dropdownRef.current.querySelector(`[data-value="${filteredOptions[idx].value}"]`);
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen, filteredOptions]);

  return {
    isOpen,
    isEditing,
    inputValue,
    highlightedIndex,
    dropdownStyle,
    filteredOptions,
    dropdownRef,
    triggerRef,
    inputRef,
    containerEl,
    open,
    close,
    handleSelect,
    handleKeyDown,
    handleInputChange,
    commitTypedValue,
    setIsOpen,
    setIsEditing,
    setInputValue,
    setHighlightedIndex,
    updatePosition,
  };
};
